import { useMemo } from 'react';
import type { AxiosResponse } from 'axios';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';

import { ButtonField } from '@wk/components-react16';

import { IconFileText } from '@tabler/icons-react';

import { fetchNewsletterPublic } from '@/domains/newsletter/newsletterApi';
import { getReleaseNoteAuthorName } from '@/features/newsletter/lib/author';
import type { NewsletterPage } from '@/domains/newsletter/newsletter.types';
import type { UserRoleAccess } from '@/types';
import { UserRole } from '@/types';
import './NewsletterPublicPage.css';

const NewsletterPublic = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const userRoleAccess = queryClient.getQueryData<AxiosResponse<UserRoleAccess>>([
    'userRoleAccess',
  ]);

  const canManage =
    userRoleAccess?.data?.user?.roles?.includes(UserRole.RELEASE_NOTES_MANAGER) ??
    false;

  const { data: pages = [], isLoading } = useQuery({
    queryKey: ['newsletter', 'public'],
    queryFn: async () => {
      const response = await fetchNewsletterPublic();
      return response.data;
    },
  });

  const visiblePages = useMemo(() => {
    return pages
      .slice()
      .filter((page) => page.published)
      .sort(
        (a, b) =>
          new Date(b.startAt).getTime() - new Date(a.startAt).getTime(),
      );
  }, [pages]);

  const formatDateTime = (value: string) =>
    new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(value));

  return (
    <div className="flex flex-col gap-4 h-full min-h-0 max-sm:h-auto">
      <div className="flex items-center justify-between gap-2 max-sm:flex-wrap">
        <div className="flex items-center gap-2">
          <IconFileText size={24} />
          <h1 className="text-2xl font-bold">Newsletter</h1>
        </div>
        {canManage ? (
          <ButtonField mode="text">
            <button type="button" onClick={() => navigate('/newsletter/admin')}>
              Manage
            </button>
          </ButtonField>
        ) : null}
      </div>

      <div className="flex flex-col gap-4 flex-1 min-h-0 overflow-auto pr-1 max-sm:flex-none max-sm:overflow-visible max-sm:pr-0">
        {isLoading ? (
          <div className="border rounded-lg p-4 bg-white shadow-sm text-sm text-gray-700">
            <p>Loading published newsletters...</p>
          </div>
        ) : visiblePages.length === 0 ? (
          <div className="border rounded-lg p-4 bg-white shadow-sm text-sm text-gray-700">
            <p>No published newsletters are available right now.</p>
          </div>
        ) : (
          visiblePages.map((page: NewsletterPage) => (
            <article
              key={page.id}
              className="border rounded-lg p-4 bg-white shadow-sm flex flex-col gap-3 max-sm:p-3"
            >
              <div className="flex flex-col gap-1">
                <p className="text-xs text-gray-500">
                  Author {getReleaseNoteAuthorName(page)}
                </p>
                <p className="text-xs text-gray-500">
                  Published {formatDateTime(page.startAt)}
                </p>
                <p className="text-xs text-gray-500">
                  Updated {formatDateTime(page.updatedAt)}
                </p>
              </div>
              <h2 className="text-xl font-semibold">{page.title || 'Untitled'}</h2>
              <div
                className="newsletter-content text-sm leading-relaxed space-y-3"
                dangerouslySetInnerHTML={{
                  __html: page.content || '<p>No content.</p>',
                }}
              />
            </article>
          ))
        )}
      </div>
    </div>
  );
};

export default NewsletterPublic;
